/**
 * Admin Feedback Screen - MOBILE ONLY
 * 
 * Review and manage user feedback submissions.
 * Shows stats, filter by status, and actions to update status.
 */

import React, { useCallback } from 'react';
import { View, Text, ScrollView, StyleSheet, Pressable, RefreshControl, ActivityIndicator, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter, Redirect } from 'expo-router';
import { useAuthStore } from '../../../store/authStore';
import { useFeedback } from '../../../hooks/useFeedback';
import { Feedback, FeedbackStatus, FeedbackType, FeedbackPriority } from '../../../shared/types/feedback';
import { color, text, space, radius, touch, icon, layout, border } from '../../../shared/tokens/mobile';
import { ScreenContainer, SectionHeader, ListCard } from '../../../components/v2/mobile';

const STATUS_OPTIONS: Array<{ value: FeedbackStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'submitted', label: 'New' },
  { value: 'acknowledged', label: 'Ack' },
  { value: 'in_progress', label: 'Progress' },
  { value: 'resolved', label: 'Resolved' },
];

const TYPE_ICONS: Record<FeedbackType, keyof typeof Ionicons.glyphMap> = {
  bug: 'bug-outline',
  feature_request: 'bulb-outline',
  ui_ux: 'color-palette-outline',
  performance: 'speedometer-outline',
  accessibility: 'accessibility-outline',
  general: 'chatbubble-outline',
  other: 'help-circle-outline',
};

const PRIORITY_COLORS: Record<FeedbackPriority, string> = {
  critical: color.error,
  high: color.warning,
  medium: color.info,
  low: color.textMuted,
};

export default function AdminFeedbackScreen() {
  const router = useRouter();
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'admin';
  const { feedbackList, stats, loading, error, filter, setFilter, refresh, updateFeedbackStatus } = useFeedback();

  const handleBack = useCallback(() => router.back(), [router]);

  const handleStatusChange = useCallback((item: Feedback) => {
    const nextStatus: Record<FeedbackStatus, FeedbackStatus> = {
      submitted: 'acknowledged',
      acknowledged: 'in_progress',
      in_progress: 'resolved',
      resolved: 'closed',
      closed: 'closed',
    };
    
    const next = nextStatus[item.status];
    if (next === item.status) return;
    
    Alert.alert('Update Status', `Mark as "${next}"?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Update', onPress: () => updateFeedbackStatus(item.id, next) },
    ]);
  }, [updateFeedbackStatus]);

  if (!isAdmin) {
    return <Redirect href="/v2/home" />;
  }

  return (
    <ScreenContainer>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={refresh} tintColor={color.primary} />}
      >
        <View style={styles.header}>
          <Pressable onPress={handleBack} style={styles.backButton} accessibilityRole="button">
            <Ionicons name="chevron-back" size={icon.lg} color={color.textPrimary} />
          </Pressable>
          <Text style={styles.title}>User Feedback</Text>
          <View style={styles.spacer} />
        </View>

        {/* STATS */}
        {stats && (
          <View style={styles.statsRow}>
            <StatBadge label="Total" value={stats.total} />
            <StatBadge label="Bugs" value={stats.byType.bug || 0} warn={(stats.byType.bug || 0) > 0} />
            <StatBadge label="Features" value={stats.byType.feature_request || 0} />
            <StatBadge label="Critical" value={stats.byPriority.critical || 0} warn={(stats.byPriority.critical || 0) > 0} />
          </View>
        )}

        {/* STATUS FILTER */}
        <View style={styles.filterContainer}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filterRow}>
            {STATUS_OPTIONS.map((opt) => (
              <Pressable
                key={opt.value}
                onPress={() => setFilter(opt.value)}
                style={[styles.filterChip, filter === opt.value && styles.filterChipActive]}
              >
                <Text style={[styles.filterChipText, filter === opt.value && styles.filterChipTextActive]}>
                  {opt.label}
                </Text>
              </Pressable>
            ))}
          </ScrollView>
        </View>

        {/* FEEDBACK LIST */}
        <View style={styles.section}>
          {loading && feedbackList.length === 0 ? (
            <View style={styles.loadingState}>
              <ActivityIndicator size="large" color={color.primary} />
            </View>
          ) : feedbackList.length === 0 ? (
            <ListCard>
              <View style={styles.emptyState}>
                <Ionicons name="chatbubbles-outline" size={icon.xxl} color={color.textMuted} />
                <Text style={styles.emptyText}>No feedback {filter !== 'all' ? `with "${filter}" status` : 'yet'}</Text>
              </View>
            </ListCard>
          ) : (
            <ListCard>
              {feedbackList.map((item, index) => (
                <FeedbackRow
                  key={item.id}
                  item={item}
                  onPress={() => handleStatusChange(item)}
                  hideBorder={index === feedbackList.length - 1}
                />
              ))}
            </ListCard>
          )}
        </View>
      </ScrollView>
    </ScreenContainer>
  );
}

// =============================================================================
// STAT BADGE
// =============================================================================

function StatBadge({ label, value, warn }: { label: string; value: number; warn?: boolean }) {
  return (
    <View style={[styles.statBadge, warn && styles.statBadgeWarn]}>
      <Text style={[styles.statBadgeValue, warn && styles.statBadgeValueWarn]}>{value}</Text>
      <Text style={styles.statBadgeLabel}>{label}</Text>
    </View>
  );
}

// =============================================================================
// FEEDBACK ROW
// =============================================================================

function FeedbackRow({ item, onPress, hideBorder }: { item: Feedback; onPress: () => void; hideBorder: boolean }) {
  const typeIcon = TYPE_ICONS[item.type] || 'help-circle-outline';
  const priorityColor = PRIORITY_COLORS[item.priority] || color.textMuted;
  const date = new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <Pressable onPress={onPress} style={({ pressed }) => [styles.feedbackRow, !hideBorder && styles.feedbackRowBorder, pressed && styles.feedbackRowPressed]}>
      <View style={styles.feedbackIcon}>
        <Ionicons name={typeIcon} size={icon.md} color={color.textSecondary} />
      </View>
      <View style={styles.feedbackContent}>
        <Text style={styles.feedbackTitle} numberOfLines={1}>{item.title}</Text>
        <Text style={styles.feedbackMeta} numberOfLines={1}>
          {item.type.replace('_', ' ')} • {date}
        </Text>
      </View>
      <View style={styles.feedbackRight}>
        <View style={[styles.priorityDot, { backgroundColor: priorityColor }]} />
        <Text style={styles.statusText}>{item.status.replace('_', ' ')}</Text>
      </View>
    </Pressable>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  scrollContent: { paddingBottom: layout.scrollBottomPadding },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: space.sm, paddingVertical: space.sm },
  backButton: { width: touch.minimum, height: touch.minimum, alignItems: 'center', justifyContent: 'center' },
  title: { flex: 1, fontSize: text.body.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textPrimary, textAlign: 'center' },
  spacer: { width: touch.minimum },
  section: { paddingHorizontal: space.md, marginBottom: space.lg },
  statsRow: { flexDirection: 'row', paddingHorizontal: space.md, marginBottom: space.md, gap: space.sm },
  statBadge: { flex: 1, backgroundColor: color.surface, borderRadius: radius.md, padding: space.sm, alignItems: 'center', borderWidth: 1, borderColor: color.border },
  statBadgeWarn: { backgroundColor: color.errorLight, borderColor: color.error },
  statBadgeValue: { fontSize: text.cardTitle.fontSize, fontWeight: text.cardTitle.fontWeight, color: color.textPrimary },
  statBadgeValueWarn: { color: color.error },
  statBadgeLabel: { fontSize: text.caption.fontSize - 1, color: color.textSecondary, marginTop: 2 },
  filterContainer: { marginBottom: space.md },
  filterRow: { paddingHorizontal: space.md, gap: space.xs },
  filterChip: { paddingVertical: space.xs, paddingHorizontal: space.sm + space.xs, borderRadius: radius.full, backgroundColor: color.backgroundSecondary },
  filterChipActive: { backgroundColor: color.primary },
  filterChipText: { fontSize: text.caption.fontSize, fontWeight: '500', color: color.textSecondary },
  filterChipTextActive: { color: color.textInverse },
  loadingState: { paddingVertical: space.xxl, alignItems: 'center' },
  emptyState: { paddingVertical: space.xxl, alignItems: 'center', gap: space.md },
  emptyText: { fontSize: text.body.fontSize, color: color.textMuted },
  feedbackRow: { flexDirection: 'row', alignItems: 'center', padding: space.md, gap: space.sm },
  feedbackRowBorder: { borderBottomWidth: border.width.thin, borderBottomColor: color.divider },
  feedbackRowPressed: { backgroundColor: color.pressed },
  feedbackIcon: { width: 32, height: 32, borderRadius: 16, backgroundColor: color.backgroundSecondary, alignItems: 'center', justifyContent: 'center' },
  feedbackContent: { flex: 1 },
  feedbackTitle: { fontSize: text.body.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textPrimary },
  feedbackMeta: { fontSize: text.caption.fontSize, color: color.textSecondary, marginTop: 2, textTransform: 'capitalize' },
  feedbackRight: { alignItems: 'flex-end', gap: space.xs },
  priorityDot: { width: 8, height: 8, borderRadius: 4 },
  statusText: { fontSize: text.caption.fontSize - 1, color: color.textMuted, textTransform: 'capitalize' },
});
